import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const { isAuthenticated } = useAuth();

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/services', label: t.nav.services },
    { path: '/membership', label: t.nav.membership },
    { path: '/events', label: t.nav.events },
    { path: '/contact', label: t.nav.contact }
  ];

  return (
    <nav style={{
      background: 'var(--bg-primary)',
      borderBottom: '1px solid var(--border-light)',
      padding: '20px 40px',
      position: 'sticky',
      top: 0,
      zIndex: 100
    }}>
      <div className="container" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: '1400px',
        padding: 0
      }}>
        {/* Logo */}
        <Link to="/" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          textDecoration: 'none'
        }}>
          <span style={{
            fontSize: '20px',
            fontWeight: 400,
            color: 'var(--text-primary)'
          }}>
            JSX International
          </span>
          <img 
            src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/swkyh5by_Logo_JSX.png"
            alt="JSX Logo"
            style={{
              height: '45px',
              width: 'auto'
            }}
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/i3m4sma4_Logo%20club%20hetG.jpg"
            alt="Henry & Gaby Logo"
            style={{
              height: '45px',
              width: 'auto'
            }}
          />
        </Link>

        {/* Desktop Navigation */}
        <ul style={{
          display: 'flex',
          alignItems: 'center',
          gap: '32px',
          listStyle: 'none',
          margin: 0,
          padding: 0
        }} className="desktop-nav">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                style={{
                  color: 'var(--text-primary)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: 400,
                  padding: '12px 0',
                  position: 'relative',
                  display: 'inline-block'
                }}
                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            </li>
          ))}
          
          {/* Language Toggle */}
          <li>
            <button
              onClick={toggleLanguage}
              style={{
                background: 'none',
                border: '1px solid var(--border-light)',
                borderRadius: '0px',
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: 'var(--text-primary)',
                transition: 'border-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.borderColor = 'var(--text-primary)'}
              onMouseLeave={(e) => e.target.style.borderColor = 'var(--border-light)'}
            >
              <Globe size={14} />
              {language.toUpperCase()}
            </button>
          </li>

          {/* Member Account Button */}
          <li>
            <Link
              to={isAuthenticated ? "/member/dashboard" : "/member-login"}
              style={{
                background: 'var(--text-primary)',
                color: 'var(--bg-primary)',
                border: 'none',
                borderRadius: '0px',
                padding: '8px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                textDecoration: 'none',
                fontWeight: 600,
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = 'var(--interactive-hover)'}
              onMouseLeave={(e) => e.target.style.background = 'var(--text-primary)'}
            >
              <User size={14} />
              {language === 'fr' ? 'Mon Compte' : 'My Account'}
            </Link>
          </li>
        </ul>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'none'
          }}
          className="mobile-menu-btn"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: 'var(--bg-primary)',
          borderBottom: '1px solid var(--border-light)',
          padding: '20px 40px'
        }} className="mobile-nav">
          <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  style={{
                    color: 'var(--text-primary)',
                    textDecoration: 'none',
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    padding: '8px 0'
                  }}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li>
              <button
                onClick={toggleLanguage}
                style={{
                  background: 'none',
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  color: 'var(--text-primary)'
                }}
              >
                <Globe size={14} />
                {language.toUpperCase()}
              </button>
            </li>
            {/* Mon Compte - Mobile */}
            <li style={{ marginTop: '8px' }}>
              <Link
                to={isAuthenticated ? "/member/dashboard" : "/member-login"}
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'var(--text-primary)',
                  color: 'var(--bg-primary)',
                  border: 'none',
                  borderRadius: '0px',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  textDecoration: 'none',
                  fontWeight: 600
                }}
              >
                <User size={16} />
                {language === 'fr' ? 'Mon Compte' : 'My Account'}
              </Link>
            </li>
          </ul>
        </div>
      )}

      <style jsx>{`
        .nav-link:after {
          content: '';
          position: absolute;
          bottom: 8px;
          left: 0;
          width: 0;
          height: 1px;
          background: var(--text-primary);
          transition: width 0.5s ease-in-out;
        }
        
        .nav-link:hover:after,
        .nav-link.active:after {
          width: 100%;
        }
        
        @media (max-width: 1024px) {
          .desktop-nav {
            display: none !important;
          }
          
          .mobile-menu-btn {
            display: block !important;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navigation;